import { Typography } from '@material-tailwind/react'
import { useTranslation } from 'react-i18next'
import { useLabelsContext } from '../contexts/LabelsContext'
import { AgamiLibraryResponse } from '../types/agami-library'

interface AgamiLibrarySummaryProps {
  facets: AgamiLibraryResponse['facets']
}

const SummaryGroup: React.FC<{ label: string; values: string[] }> = ({ label, values }) => (
  <div className="inline-flex min-w-0 flex-wrap items-baseline gap-x-2">
    <Typography variant="small" className="font-bold dark:text-gray-100">
      {label}
    </Typography>
    <Typography variant="small" className="text-blue-gray-700 dark:text-gray-200">
      {values.join(' · ')}
    </Typography>
  </div>
)

export const AgamiLibrarySummary: React.FC<AgamiLibrarySummaryProps> = ({ facets }) => {
  const { t } = useTranslation()
  const { getSpotTypeLabel } = useLabelsContext()

  const statusValues = [
    t('agamiLibrary.summary.total', { count: facets.status.all }),
    t('agamiLibrary.summary.published', { count: facets.status.published }),
    t('agamiLibrary.summary.drafts', { count: facets.status.drafts }),
  ]
  if (facets.status.proposals > 0) {
    statusValues.push(t('agamiLibrary.summary.proposals', { count: facets.status.proposals }))
  }

  const mediaValues = facets.media.map((facet) => `${t(`agamiLibrary.media.${facet.value}`)} ${facet.count}`)
  const activationValues = facets.activation.map(
    (facet) => `${getSpotTypeLabel(Number(facet.value)) ?? facet.value} ${facet.count}`
  )
  const languageValues = facets.languages.map((facet) => {
    const value = String(facet.value)
    const label = value === 'undeclared' ? t('agamiLibrary.languages.undeclared') : value.toUpperCase()
    return `${label} ${facet.count}`
  })

  return (
    <section
      className="flex flex-wrap items-center gap-x-6 gap-y-1.5 rounded-xl bg-blue-gray-50 px-3 py-2 dark:bg-primary-600 mb-5"
      aria-label={t('agamiLibrary.summary.label')}
      data-testid="agami-library-summary"
    >
      <SummaryGroup label={t('agamiLibrary.summary.status')} values={statusValues} />
      <SummaryGroup label={t('agamiLibrary.summary.media')} values={mediaValues} />
      <SummaryGroup label={t('agamiLibrary.summary.activation')} values={activationValues} />
      <SummaryGroup label={t('agamiLibrary.summary.languages')} values={languageValues} />
    </section>
  )
}
